import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  Plus,
  Trash2,
  Award,
} from "lucide-react";
import {
  createGradeScheme,
  getAllGradeSchemes,
  deleteGradeScheme,
} from "../../api/reportcard/reportCardApi";
import {
  createGradeSchemePortal,
  getAllGradeSchemesPortal,
  deleteGradeSchemePortal,
} from "../../api/reportcard/reportCardPortalApi";
import { useStaffAuth } from "../../context/StaffAuthContext";
import {
  DEFAULT_GRADE_BANDS,
  pickList,
  toId,
} from "../../utils/reportcard/reportCardHelpers";
import * as UI from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const Card =
  UI.Card ||
  (({ className, children, ...props }) => (
    <div
      className={`bg-white dark:bg-neutral-900 border border-primary-100 rounded-2xl shadow-xs ${className}`}
      {...props}
    >
      {children}
    </div>
  ));

const Button =
  UI.Button ||
  (({ children, className, ...props }) => (
    <button
      className={`inline-flex items-center justify-center px-3.5 py-2 text-xs font-semibold rounded-xl transition-all cursor-pointer ${className}`}
      {...props}
    >
      {children}
    </button>
  ));

const Badge =
  UI.Badge ||
  (({ children, className }) => (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${className}`}
    >
      {children}
    </span>
  ));

const emptyBand = () => ({
  minPercentage: "",
  maxPercentage: "",
  grade: "",
  label: "",
  gradePoint: "",
});

const GradeSchemeList = ({ isPortal = false }) => {
  const navigate = useNavigate();
  const staffAuth = useStaffAuth();
  const canManage = isPortal
    ? staffAuth?.hasPermission?.("REPORT_CARD_FULL")
    : true;
  const base = isPortal ? "/staff-panel/report-cards" : "/report-cards";

  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const toast = useToast();

  const [form, setForm] = useState({
    name: "",
    description: "",
    passPercentage: "33",
    subjectPassPercentage: "33",
    percentageDecimals: "2",
    isDefault: false,
    gradeBands: DEFAULT_GRADE_BANDS.map((b) => ({ ...b })),
  });

  const load = async () => {
    try {
      setLoading(true);
      const res = isPortal
        ? await getAllGradeSchemesPortal({ isActive: "true" })
        : await getAllGradeSchemes({ isActive: "true" });
      setSchemes(pickList(res, ["schemes", "data"]));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load grade schemes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPortal]);

  const updateBand = (idx, key, val) => {
    setForm((f) => {
      const bands = [...f.gradeBands];
      bands[idx] = { ...bands[idx], [key]: val };
      return { ...f, gradeBands: bands };
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        name: form.name.trim(),
        description: form.description,
        passPercentage: Number(form.passPercentage),
        subjectPassPercentage: Number(form.subjectPassPercentage),
        percentageDecimals: Number(form.percentageDecimals),
        isDefault: form.isDefault,
        gradeBands: form.gradeBands.map((b) => ({
          minPercentage: Number(b.minPercentage),
          maxPercentage: Number(b.maxPercentage),
          grade: String(b.grade).trim(),
          label: b.label || "",
          gradePoint:
            b.gradePoint === "" || b.gradePoint == null
              ? null
              : Number(b.gradePoint),
        })),
      };
      if (isPortal) await createGradeSchemePortal(payload);
      else await createGradeScheme(payload);
      toast.success("Grade scheme created");
      setShowForm(false);
      setForm({
        name: "",
        description: "",
        passPercentage: "33",
        subjectPassPercentage: "33",
        percentageDecimals: "2",
        isDefault: false,
        gradeBands: DEFAULT_GRADE_BANDS.map((b) => ({ ...b })),
      });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deactivate this grade scheme?")) return;
    try {
      if (isPortal) await deleteGradeSchemePortal(id);
      else await deleteGradeScheme(id);
      toast.success("Scheme removed");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed");
    }
  };

  return (
    <div className="min-h-screen bg-primary-50/40 p-4 sm:p-6 lg:p-8 antialiased">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-neutral-900 p-6 rounded-2xl border border-primary-100 shadow-sm">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-primary-600 dark:text-primary-400">
              Report Cards
            </span>
            <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight mt-1">
              Grade Schemes
            </h1>
            <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 mt-1 max-w-xl">
              Define passing thresholds, grade intervals, and grade points for report cards.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => navigate(`${base}/templates`)}
              className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800"
            >
              Templates
            </Button>
            <Button
              type="button"
              onClick={() => navigate(base)}
              className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800"
            >
              Batches
            </Button>
            {canManage && (
              <Button
                type="button"
                onClick={() => setShowForm((v) => !v)}
                className="bg-primary-600 hover:bg-primary-700 text-white shadow-xs"
              >
                <Plus className="h-4 w-4 mr-1.5" /> New Scheme
              </Button>
            )}
          </div>
        </div>

        {/* New Scheme Creation Card */}
        {showForm && canManage && (
          <Card className="border-primary-200 overflow-hidden shadow-md">
            <div className="border-b border-primary-100 bg-primary-50/50 p-6">
              <h2 className="text-base font-bold text-neutral-900 dark:text-neutral-100">
                New Grade Scheme Configuration
              </h2>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                Setup threshold rules and customizable letter grades.
              </p>
            </div>
            <div className="p-6">
              <form onSubmit={handleCreate} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="sm:col-span-2 space-y-1.5">
                    <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">
                      Scheme Name *
                    </label>
                    <input
                      type="text"
                      className="w-full h-9 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-3 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="e.g. CBSE Standard 10-Point System"
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">
                      Overall Pass %
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      className="w-full h-9 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-3 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={form.passPercentage}
                      onChange={(e) => setForm({ ...form, passPercentage: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">
                      Subject Pass %
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      className="w-full h-9 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-3 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={form.subjectPassPercentage}
                      onChange={(e) => setForm({ ...form, subjectPassPercentage: e.target.value })}
                    />
                  </div>

                  <div className="sm:col-span-2 space-y-1.5">
                    <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">
                      Description
                    </label>
                    <input
                      type="text"
                      className="w-full h-9 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-3 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="e.g. Standard grading policy for secondary wing"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">
                      % Decimals Precision
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={4}
                      className="w-full h-9 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-3 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={form.percentageDecimals}
                      onChange={(e) => setForm({ ...form, percentageDecimals: e.target.value })}
                    />
                  </div>

                  <div className="flex items-end pb-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="isDefault"
                        className="h-4 w-4 rounded border-slate-300 dark:border-neutral-700 text-primary-600 dark:text-primary-400 focus:ring-primary-500"
                        checked={form.isDefault}
                        onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                      />
                      <label htmlFor="isDefault" className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 cursor-pointer">
                        Set as default scheme
                      </label>
                    </div>
                  </div>
                </div>

                {/* Grade Bands Section */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-center justify-between border-b border-neutral-200 dark:border-neutral-800 pb-2">
                    <div>
                      <h3 className="text-xs font-bold text-neutral-900 dark:text-neutral-100 uppercase tracking-wider">
                        Grade Bands & Boundaries
                      </h3>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">Configure percentage intervals and grade tags</p>
                    </div>
                    <Button
                      type="button"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          gradeBands: [...f.gradeBands, emptyBand()],
                        }))
                      }
                      className="text-primary-600 dark:text-primary-400 hover:bg-primary-50 border border-primary-200 py-1"
                    >
                      + Add Band
                    </Button>
                  </div>

                  <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-x-auto">
                    <table className="w-full text-xs text-left border-collapse">
                      <thead className="bg-neutral-50 dark:bg-neutral-950 border-b border-neutral-200 dark:border-neutral-800 text-neutral-500 dark:text-neutral-400 font-semibold uppercase tracking-wider">
                        <tr>
                          <th className="py-2.5 px-3 w-24">Min %</th>
                          <th className="py-2.5 px-3 w-24">Max %</th>
                          <th className="py-2.5 px-3 w-24">Grade</th>
                          <th className="py-2.5 px-3">Label</th>
                          <th className="py-2.5 px-3 w-20">GP</th>
                          <th className="py-2.5 px-3 w-12 text-right" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-neutral-800">
                        {form.gradeBands.map((b, i) => (
                          <tr key={i}>
                            <td className="p-2">
                              <input
                                type="number"
                                className="w-full h-8 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-2 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                value={b.minPercentage}
                                onChange={(e) => updateBand(i, "minPercentage", e.target.value)}
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="number"
                                className="w-full h-8 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-2 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                value={b.maxPercentage}
                                onChange={(e) => updateBand(i, "maxPercentage", e.target.value)}
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="text"
                                className="w-full h-8 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-2 uppercase font-bold text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                value={b.grade}
                                onChange={(e) => updateBand(i, "grade", e.target.value)}
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="text"
                                className="w-full h-8 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-2 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                value={b.label}
                                onChange={(e) => updateBand(i, "label", e.target.value)}
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="number"
                                className="w-full h-8 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-xs px-2 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                value={b.gradePoint ?? ""}
                                onChange={(e) => updateBand(i, "gradePoint", e.target.value)}
                              />
                            </td>
                            <td className="p-2 text-right">
                              <button
                                type="button"
                                className="h-8 w-8 inline-flex items-center justify-center text-neutral-400 dark:text-neutral-500 hover:text-rose-600 transition-colors cursor-pointer"
                                onClick={() =>
                                  setForm((f) => ({
                                    ...f,
                                    gradeBands: f.gradeBands.filter((_, j) => j !== i),
                                  }))
                                }
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-neutral-200 dark:border-neutral-800">
                  <Button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-primary-600 hover:bg-primary-700 text-white"
                  >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
                    Save Scheme
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        )}

        {/* List of Grade Schemes */}
        <Card className="overflow-hidden bg-white dark:bg-neutral-900 border-primary-100 shadow-sm">
          <div className="border-b border-neutral-200 dark:border-neutral-800 bg-primary-50/30 px-6 py-4 flex items-center gap-2 text-sm font-bold text-neutral-900 dark:text-neutral-100">
            <Award className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            Configured Grade Schemes
          </div>
          <div>
            {loading ? (
              <div className="p-16 flex flex-col items-center justify-center text-neutral-400 dark:text-neutral-500 gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-400" />
                <span className="text-xs font-semibold">Loading schemes...</span>
              </div>
            ) : schemes.length === 0 ? (
              <div className="p-12 text-center text-xs text-neutral-500 dark:text-neutral-400">
                No grade schemes yet. Create one before configuring templates.
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-neutral-800">
                {schemes.map((s) => (
                  <div key={toId(s)} className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-neutral-900 dark:text-neutral-100 text-sm sm:text-base">
                          {s.name}
                        </span>
                        {s.isDefault && (
                          <Badge className="bg-primary-100 text-primary-800 dark:text-primary-300">
                            Default
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        Overall Pass: <strong className="text-neutral-700 dark:text-neutral-200">{s.passPercentage}%</strong>
                        {s.subjectPassPercentage != null ? ` · Subject Pass: ${s.subjectPassPercentage}%` : ""}
                        {s.description ? ` · ${s.description}` : ""}
                      </p>
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {(s.gradeBands || []).slice(0, 8).map((b, i) => (
                          <span
                            key={i}
                            className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-slate-100 dark:bg-neutral-900 text-neutral-700 dark:text-neutral-200"
                          >
                            <strong>{b.grade}</strong>: {b.minPercentage}–{b.maxPercentage}%
                          </span>
                        ))}
                      </div>
                    </div>
                    {canManage && (
                      <button
                        type="button"
                        onClick={() => handleDelete(toId(s))}
                        className="text-xs font-semibold text-rose-600 hover:text-rose-700 hover:bg-rose-50 px-3 py-1.5 rounded-lg transition-colors self-start sm:self-center cursor-pointer"
                      >
                        Deactivate
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default GradeSchemeList;